<template>
  <div class="input-builder">
    <InputError :errors="errors">
      <label v-if="label" :for="name">
        <slot name="label">
          {{ label }}
          <sup v-if="required && label">
            *
          </sup>
        </slot>
      </label>
      <slot>
        <div
          :class="[
            'input-builder__container',
            { 'input-builder__container--has-icon': icon },
            { 'input-builder__container--has-action-icon': actionIcon }
          ]"
        >
          <span v-if="icon" class="input-builder__icon" v-html="decodeSvgDataUrl(icon)" />
          <input
            v-if="inputType === 'input'"
            :id="name"
            ref="field"
            :name="name"
            :value="value"
            :placeholder="placeholder"
            :disabled="disabled"
            :type="type"
            :autofocus="autofocus"
            :tabindex="tabindex"
            :maxlength="maxlength"
            :autocomplete="autocomplete ? 'on' : 'off'"
            :class="{ 'input-builder--align-right': valueAlign === 'right' }"
            @input="updateValue($event.target.value)"
          >
          <textarea
            v-else-if="inputType === 'textarea'"
            :id="name"
            ref="field"
            :name="name"
            :placeholder="placeholder"
            :disabled="disabled"
            :autofocus="autofocus"
            :tabindex="tabindex"
            :value="value"
            :maxlength="maxlength"
            :autocomplete="autocomplete ? 'on' : 'off'"
            :class="{ 'input-builder--align-right': valueAlign === 'right' }"
            @input="updateValue($event.target.value)"
          />
          <button
            v-if="actionIcon"
            type="button"
            :class="actionIconClasses"
            :disabled="disabled"
            :aria-label="actionIconLabel"
            :title="actionIconLabel"
            @click="$emit('action-click')"
            v-html="decodeSvgDataUrl(actionIcon)"
          />
        </div>
      </slot>
      <small v-if="hint.length > 0">
        {{ hint }}
      </small>
    </InputError>
  </div>
</template>

<script>
import InputError from './InputError.vue';
import decodeSvgDataUrl from '../../../../utils/decodeSvgDataUrl';

export default {
  components: {
    InputError,
  },
  props: {
    errors: {
      type: Array,
      default() {
        return [];
      },
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    value: {
      type: [String, Number],
      required: false,
      default: '',
    },
    name: {
      type: String,
      required: false,
      default: '',
    },
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    hint: {
      type: String,
      required: false,
      default: '',
    },
    label: {
      type: String,
      required: false,
      default: '',
    },
    type: {
      type: String,
      required: false,
      default: 'text',
    },
    inputType: {
      type: String,
      required: false,
      default: 'input',
      validator: value => ['input', 'textarea'].includes(value),
    },
    tabindex: {
      type: String,
      required: false,
      default: '',
    },
    autofocus: {
      type: Boolean,
      required: false,
      default: false,
    },
    autocomplete: {
      type: Boolean,
      required: false,
      default: true,
    },
    maxlength: {
      type: Number,
      default: null,
    },
    valueAlign: {
      type: String,
      default: 'left',
    },
    icon: {
      type: String,
      required: false,
      default: '',
    },
    actionIcon: {
      type: String,
      required: false,
      default: '',
    },
    actionIconLabel: {
      type: String,
      required: false,
      default: '',
    },
    actionIconKind: {
      type: String,
      required: false,
      default: 'regular',
      validator: value => ['regular', 'negative'].includes(value),
    },
    actionIconOnHover: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    actionIconClasses() {
      return [
        'input-builder__action-icon',
        `input-builder__action-icon--${this.actionIconKind}`,
        { 'input-builder__action-icon--on-hover': this.actionIconOnHover },
      ];
    },
  },
  mounted() {
    if (!this.autofocus) return;

    this.$nextTick(() => {
      const field = this.$refs.field;
      if (field) field.focus();
    });
  },
  methods: {
    updateValue(value) {
      this.$emit('input', value);
    },
    decodeSvgDataUrl,
  },
};
</script>

<style lang="scss">
.input-builder {
  &__container {
    @apply flex relative items-center;

    &--has-icon {
      input {
        @apply pl-10 #{!important};
      }
    }

    // The action icon renders for textareas too, so both need to keep clear of it.
    &--has-action-icon {
      input,
      textarea {
        @apply pr-10 #{!important};
      }
    }
  }

  &__icon {
    @apply absolute left-4 top-1/2 flex items-center justify-center;

    transform: translateY(-50%);

    svg {
      @apply w-5 h-5 fill-neutral-600;
    }
  }

  &__action-icon {
    @apply absolute right-4 top-1/2 flex items-center justify-center p-0 bg-transparent border-none cursor-pointer;

    transform: translateY(-50%);

    svg {
      @apply w-5 h-5;
    }

    &--regular svg,
    &--regular svg path {
      @apply fill-neutral-600;
    }

    &--negative svg,
    &--negative svg path {
      @apply fill-error-500;
    }

    &:disabled {
      @apply cursor-not-allowed opacity-30;
    }

    &--on-hover {
      @apply opacity-0 transition-opacity;
    }
  }

  &__container:hover &__action-icon--on-hover,
  &__container:focus-within &__action-icon--on-hover {
    @apply opacity-100;
  }
}

input {
  outline: none;

  &.input-builder--align-right {
    text-align: right;
  }
}

sup {
  color: #ff2373;
}
</style>
